import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import * as LoginSignupEvents from 'app/ui/layout/login-signup-events';
import * as loginAnalytics from 'app/ui/layout/login-signup-analytics';
import { Analytics } from 'packages';
import {
    useCurrentLanguage,
    useTranslator,
    useUrlLocalizer
} from 'tradera-lang/translate';
import { loadScript } from 'tradera-utils/script';
import FacebookIcon from '@tradera/blueprint/theme/icons2/facebook.svg?jsx';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';
import { getLoginLocaleFromLanguage } from './get-login-locale-from-language';

const FacebookLoginButton = props => {
    const {
        facebookAppId,
        className,
        compact,
        disabled,
        isModal,
        onError,
        tabIndex
    } = props;
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const { language } = useCurrentLanguage();
    const locale = getLoginLocaleFromLanguage(language);

    const onClick = async () => {
        if (disabled) {
            return;
        }

        setIsLoading(true);
        onError(null);
        try {
            await loadScript(
                `//connect.facebook.net/${locale || 'sv_SE'}/sdk.js`,
                {
                    timeout: 5000
                }
            );
            if (window.FB) {
                window.FB.init({
                    appId: facebookAppId,
                    version: 'v2.9'
                });
            } else {
                throw new Error('FB_SDK_LOAD_FAILED');
            }
        } catch (error) {
            onError(error);
            setIsLoading(false);
            return;
        }

        if (isModal) {
            Analytics.trackEvent({
                category: 'Top navigation',
                action: 'Login menu',
                label: 'Button - Facebook login'
            });
        }
        loginAnalytics.flow.set(loginAnalytics.FLOW.LOGIN);
        loginAnalytics.method.set(loginAnalytics.METHOD.FACEBOOK);
        loginAnalytics.trackEvent('user-login-submit');

        window.FB.login(
            async loginResponse => {
                if (loginResponse.status !== 'connected') {
                    return;
                }
                try {
                    const verifyResponse = await axios.post(
                        '/login/facebook/verify' + window.location.search,
                        {
                            accessToken: loginResponse.authResponse.accessToken,
                            userId: loginResponse.authResponse.userID
                        }
                    );
                    LoginSignupEvents.trackLoginSuccess(
                        verifyResponse.data.memberId
                    );
                    window.parent.location = verifyResponse.data.returnUrl;
                } catch (err) {
                    onError(err);
                    LoginSignupEvents.trackLoginFailure('facebook-auth');
                    window.parent.location = toLocalizedUrl(
                        '/login?connect=Facebook'
                    );
                }
            },
            {
                scope: 'public_profile,email'
            }
        );
        setIsLoading(false);
    };

    return (
        <button
            className={`facebook btn btn-fluid text-white ${
                compact ? '' : 'btn--icon-fixed'
            } ${disabled ? ' disabled' : ''} ${className}`}
            onClick={onClick}
            disabled={disabled}
            tabIndex={tabIndex}>
            {isLoading ? (
                <LoadingIcon
                    className={`icon ${compact ? '' : 'icon-before'} icon-spin`}
                />
            ) : (
                <FacebookIcon
                    className={`icon ${compact ? '' : 'icon-before mr-2'}`}
                />
            )}
            {!compact && t('login_withProvider', { provider: 'Facebook' })}
        </button>
    );
};

FacebookLoginButton.propTypes = {
    className: PropTypes.string,
    facebookAppId: PropTypes.string,
    isModal: PropTypes.bool,
    tabIndex: PropTypes.number
};

FacebookLoginButton.defaultProps = {
    className: '',
    tabIndex: 0
};

export default FacebookLoginButton;
